import { z } from 'zod';
import type { OperatorActionType } from './operator-action';
import type { ResourceLifecycleState } from './resource-request';

/**
 * Drone-marketplace mission LIFECYCLE — Drone D1 #319 (the engine on top of the #317
 * `drone_missions` RECORD). This module is the PURE, server-and-client-shared core of the
 * state machine: the status vocabulary, the valid from→to edges, who may drive each edge
 * (the actor guard), and the scribe verb each edge stamps. It holds NO DB / IO — the API
 * routes wrap these helpers around `withAgencyContext` + explicit two-party agency filters,
 * and the web renders the allowed actions from `missionActionsFor`.
 *
 * The flow (the #321 dispatch interaction extends #319 with confirmed + enroute): a gov
 * requestor CREATEs a mission ('requested', targeting a picked #320 operator) → the
 * OPERATOR accepts WITH TERMS (eta/price/feed) → the REQUESTOR confirms those terms → the
 * OPERATOR marks the asset enroute → flown → the REQUESTOR records billed → paid. The
 * requestor may cancel any time PRE-flown. declined / cancelled / paid are terminal.
 *
 *   requested ─accept(op)→ accepted ─confirm(req)→ confirmed ─enroute(op)→ enroute
 *      │  └decline(op)→ declined (terminal)        │                          │
 *      │                                  ─flown(op)→ flown ─bill(req)→ billed ─pay(req)→ paid
 *      └──────────── cancel(req) from {requested, accepted, confirmed, enroute} → cancelled
 *
 * The mission `status` column is free-text in the DB (the #318 convention for drone state
 * columns); this enum + FK-lookup verbs are the controlled vocabulary. Pinned by
 * `drone-mission.test.ts`.
 */

/** Every mission status. Initial is 'requested' (the #317 column default). The DB `status`
 *  column is free-text (the #318 convention) — these literals ARE the controlled vocabulary,
 *  so adding 'confirmed' / 'enroute' (#321) needs NO migration on the column itself. */
export const MISSION_STATUSES = [
  'requested',
  'accepted',
  'confirmed',
  'enroute',
  'declined',
  'cancelled',
  'flown',
  'billed',
  'paid',
] as const;

export const MissionStatusSchema = z.enum(MISSION_STATUSES);
export type MissionStatus = z.infer<typeof MissionStatusSchema>;

/**
 * Phase 4e — the requestor's declared urgency on a "request live eyes", surfaced to the drone
 * OPERATOR so they can triage accept/decline IN the COP (§I.2 counterparty ☎4). The §I.7 Q4
 * ruling: mirror agency_tasks.urgency (migration 0101) EXACTLY — routine | urgent | immediate;
 * default 'routine'. Pinned to the drone_missions_requested_urgency_check CHECK (migration 0105).
 * A display attribute (not an audited verb), so it adds NO operator-action verb.
 */
export const DRONE_REQUEST_URGENCIES = ['routine', 'urgent', 'immediate'] as const;
export const DroneRequestUrgencySchema = z.enum(DRONE_REQUEST_URGENCIES);
export type DroneRequestUrgency = z.infer<typeof DroneRequestUrgencySchema>;

/** Terminal states — no further transition is possible from these. */
export const TERMINAL_MISSION_STATUSES = ['declined', 'cancelled', 'paid'] as const;
export function isTerminalMissionStatus(status: MissionStatus): boolean {
  return (TERMINAL_MISSION_STATUSES as readonly string[]).includes(status);
}

/**
 * #322 feed-embed — the statuses during which the operator's live-video feed is shown on the
 * COP: the asset is dispatched (`enroute`) or the flight is complete (`flown`). Every other
 * state has no live feed to embed — requested/accepted/confirmed are pre-dispatch, and
 * billed/paid/declined/cancelled are post-flight or never-flew. This is the SINGLE gate the
 * server (the feed-resolution route) and the client (the COP embed) both consult, so the feed
 * can never surface before dispatch or leak after the mission winds down. */
export function isMissionFeedLive(status: MissionStatus): boolean {
  return status === 'enroute' || status === 'flown';
}

/**
 * Phase 4c — the readiness "Committed" band (operator-cop-doctrine §3B.6).
 *
 * The mission statuses that TIE UP a drone asset: an asset with a mission in one of these is
 * actively committed elsewhere, so it is NOT offered as "ready" in the marketplace. `requested`
 * is excluded (the operator may still decline — the asset is not yet committed);
 * `billed`/`paid`/`declined`/`cancelled` are post-flight or never-flew, so the asset is free again.
 */
export const COMMITTED_MISSION_STATUSES = ['accepted', 'confirmed', 'enroute', 'flown'] as const;

/** True when a mission status means the asset is actively committed (see COMMITTED_MISSION_STATUSES). */
export function isCommittedMissionStatus(status: MissionStatus): boolean {
  return (COMMITTED_MISSION_STATUSES as readonly string[]).includes(status);
}

/** The marketplace readiness band on a coverage pin / match candidate (§3B.6 readiness badge).
 *  Mission-derived 'committed' takes precedence over the asset's own availability_status. */
export type DroneReadiness = 'available' | 'committed' | 'maintenance' | 'unavailable';

/**
 * Derive the readiness band (§3B.6). PURE — mirrors the SQL CASE in drone-coverage.ts /
 * drone-match.ts (`CASE WHEN <active mission> THEN 'committed' ELSE availability_status END`)
 * exactly so the server query and any client fallback can never drift:
 *   committed  ← the asset has a mission in COMMITTED_MISSION_STATUSES (it is tasked)
 *   else       ← the asset's own availability_status (available / maintenance / unavailable)
 * `committed` wins unconditionally — a flying asset is committed regardless of its availability
 * toggle. An unrecognised availability_status (impossible under the portal Zod enum) is treated
 * conservatively as 'unavailable' rather than asserted ready.
 */
export function deriveDroneReadiness(input: {
  availabilityStatus: string;
  hasActiveMission: boolean;
}): DroneReadiness {
  if (input.hasActiveMission) return 'committed';
  switch (input.availabilityStatus) {
    case 'available':
    case 'maintenance':
    case 'unavailable':
      return input.availabilityStatus;
    default:
      return 'unavailable';
  }
}

/**
 * Phase 4d — translate a drone mission's MARKETPLACE status into the locked IMS
 * RESOURCE-LIFECYCLE band (`ResourceLifecycleState`, resource-request.ts), so Logistics/
 * Resources reads a tasked drone in the same lifecycle language as every other resource —
 * not just "Flown" (docs/coordination-rearchitecture-council-2026-06.md §I.9). PURE: the
 * mapping is presentation-only; it mints NO new state and reuses the existing band enum +
 * palette (`lifecycleStateStyle()`). Returns `null` for statuses that are NOT a resource line
 * (declined / cancelled never committed the asset), so the caller renders no band chip.
 *
 *   requested            → requested   (raised, not yet committed)
 *   accepted / confirmed → assigned    (committed to the request, not yet airborne)
 *   enroute / flown      → committed    (asset dispatched / on-station)
 *   billed / paid        → demobilized (flight done + accounted; asset free again)
 *   declined / cancelled → null         (no resource line — the asset never committed)
 *
 * RELEASE OVERRIDE (4d): an explicit operator demobilize stamps `demobilizedAt`; when set it
 * wins over the marketplace status → `demobilized`, so the band reflects the release the
 * instant Logistics frees the asset, regardless of where bill/pay sits.
 *
 * The committed-set gate REUSES `isCommittedMissionStatus` (i.e. COMMITTED_MISSION_STATUSES) —
 * never re-listed here — so a new committed status flows through automatically (defaulting to
 * `assigned` until classified airborne). The airborne split (enroute/flown → committed) is an
 * explicit local check, deliberately decoupled from the feed-embed gate `isMissionFeedLive`.
 */
export function missionStatusToLifecycleBand(
  status: MissionStatus,
  opts?: { demobilizedAt?: Date | string | null },
): ResourceLifecycleState | null {
  // 4d release override: a stamped demobilized_at reads `demobilized` over any status.
  if (opts?.demobilizedAt != null) return 'demobilized';
  if (status === 'requested') return 'requested';
  if (isCommittedMissionStatus(status)) {
    return status === 'enroute' || status === 'flown' ? 'committed' : 'assigned';
  }
  if (status === 'billed' || status === 'paid') return 'demobilized';
  // declined / cancelled — the asset never committed: no resource line.
  return null;
}

/**
 * The two parties to a mission. The actor guard is the correctness/isolation crux of #319:
 * it is enforced in the APPLICATION (the route loads the mission filtered by the acting
 * agency's column, then checks the rule's actor here), NOT just by the two-party RLS — the
 * single FOR-ALL `drone_missions` policy cannot express per-party write restrictions (the
 * #317 M2 gap, deferred to #190; enforcement-checklist §10).
 *   - requestor — the gov/EOC agency that raised the mission (requestor_agency_id).
 *   - operator  — the assigned drone operator agency (operator_agency_id).
 */
export type MissionActorRole = 'requestor' | 'operator';

/** The post-create transition actions (creation = 'request', handled separately). #321 adds
 *  confirm (requestor) + enroute (operator) between accept and flown. */
export const MISSION_ACTIONS = [
  'accept',
  'decline',
  'confirm',
  'enroute',
  'flown',
  'cancel',
  'bill',
  'pay',
] as const;
export const MissionActionSchema = z.enum(MISSION_ACTIONS);
export type MissionAction = z.infer<typeof MissionActionSchema>;

export interface MissionTransitionRule {
  /** Valid source states for this action. */
  readonly from: readonly MissionStatus[];
  /** The resulting state. */
  readonly to: MissionStatus;
  /** The ONLY party allowed to drive this action. */
  readonly actor: MissionActorRole;
  /** The operator-action scribe verb this transition stamps (FK-lookup, migration 0091). */
  readonly verb: OperatorActionType;
}

/**
 * The mission state machine — the single source of truth for valid edges, actor, and verb.
 * Typing `verb` as `OperatorActionType` makes the lockstep a COMPILE-TIME guarantee: a verb
 * missing from `OPERATOR_ACTION_TYPES` (migration 0091) fails the build here.
 */
export const MISSION_TRANSITIONS: Record<MissionAction, MissionTransitionRule> = {
  accept: { from: ['requested'], to: 'accepted', actor: 'operator', verb: 'drone_mission_accepted' },
  decline: { from: ['requested'], to: 'declined', actor: 'operator', verb: 'drone_mission_declined' },
  // #321 dispatch: the requestor confirms the operator's accept-terms (eta/price/feed), then
  // the operator marks the asset enroute. flown now follows enroute (not accept directly).
  confirm: { from: ['accepted'], to: 'confirmed', actor: 'requestor', verb: 'drone_mission_confirmed' },
  enroute: { from: ['confirmed'], to: 'enroute', actor: 'operator', verb: 'drone_mission_enroute' },
  flown: { from: ['enroute'], to: 'flown', actor: 'operator', verb: 'drone_mission_flown' },
  cancel: {
    // The requestor may cancel ANY time pre-flown (requested → enroute).
    from: ['requested', 'accepted', 'confirmed', 'enroute'],
    to: 'cancelled',
    actor: 'requestor',
    verb: 'drone_mission_cancelled',
  },
  bill: { from: ['flown'], to: 'billed', actor: 'requestor', verb: 'drone_mission_billed' },
  pay: { from: ['billed'], to: 'paid', actor: 'requestor', verb: 'drone_mission_paid' },
};

/** The scribe verb stamped on mission CREATE (the requestor raises it). */
export const MISSION_REQUESTED_VERB: OperatorActionType = 'drone_mission_requested';

/** The mission scribe verbs (lockstep with migrations 0091 + 0092): the request verb + the
 *  one verb each transition stamps. #319 seeded 7; #321 adds confirm + enroute ⇒ 9. */
export const DRONE_MISSION_VERBS: readonly OperatorActionType[] = [
  MISSION_REQUESTED_VERB,
  ...MISSION_ACTIONS.map((a) => MISSION_TRANSITIONS[a].verb),
];

/**
 * #321 dispatch — the live-video FEED KINDS an operator may attach to an accept-with-terms.
 * The operator's feed link is an HLS / RTSP / YouTube-live / WebRTC URL; this is the format
 * of the LINK, distinct from `drone_assets.downlink_type` (the asset's downlink HARDWARE
 * protocol — rtmp/srt/rtsp/webrtc). Stored free-text on `drone_missions.feed_type` (the #318
 * convention) and validated here at the API boundary. #321 only CAPTURES the link (shape-
 * validated, never fetched); #322 resolves + embeds it.
 */
export const DRONE_FEED_TYPES = ['hls', 'rtsp', 'youtube', 'webrtc', 'other'] as const;
export const DroneFeedTypeSchema = z.enum(DRONE_FEED_TYPES);
export type DroneFeedType = z.infer<typeof DroneFeedTypeSchema>;

/**
 * Operator-facing labels for a mission's live-video `feed_type` (W6 raw-enum legibility,
 * ledger §3.D) — the acronym kept, with a plain gloss where it aids a non-technical
 * reader, so a terms summary never leaks "hls feed". Surfaces in `missionTermsSummary`
 * (DroneMissionsPanel + operator-portal MissionsSection).
 */
export const DRONE_FEED_TYPE_LABELS: Record<DroneFeedType, string> = {
  hls: 'HLS (live stream)',
  rtsp: 'RTSP',
  youtube: 'YouTube',
  webrtc: 'WebRTC',
  other: 'Other feed',
};

/** Humanized `feed_type` label; `feed_type` is stored free-text, so an unrecognised
 *  value falls back to "Other feed" — never the raw token. */
export function droneFeedTypeLabel(feedType: string): string {
  return DRONE_FEED_TYPE_LABELS[feedType as DroneFeedType] ?? DRONE_FEED_TYPE_LABELS.other;
}

/**
 * #321 accept-with-terms contract — what the operator supplies when accepting a mission:
 * an ETA, a quoted price (CAD, recorded only — no charging), and a live-video feed LINK
 * (url + kind, captured-not-fetched). `feedUrl` is shape-validated to a safe scheme by the
 * route (http/https/rtsp/rtsps); nothing in #321 resolves or fetches it (the SSRF surface is
 * #322's to own when it embeds). All fields are required at accept so the requestor has full
 * terms to confirm against.
 */
export interface MissionAcceptTerms {
  readonly etaMinutes: number;
  readonly quotedPriceCad: number;
  readonly feedUrl: string;
  readonly feedType: DroneFeedType;
}

/** Which party (if any) the given agency plays on a mission. `null` ⇒ not a party. */
export function missionActorRole(
  mission: { requestorAgencyId: string; operatorAgencyId: string | null },
  agencyId: string,
): MissionActorRole | null {
  if (mission.requestorAgencyId === agencyId) return 'requestor';
  if (mission.operatorAgencyId !== null && mission.operatorAgencyId === agencyId) return 'operator';
  return null;
}

export type MissionTransitionResult =
  | { ok: true; to: MissionStatus; verb: OperatorActionType }
  | { ok: false; reason: 'invalid_transition' | 'forbidden_actor' };

/**
 * Validate a transition end-to-end: the actor must be the party the rule allows, AND the
 * mission must currently sit in a valid source state. Returns the target state + the scribe
 * verb on success. `forbidden_actor` is reported before `invalid_transition` so a wrong-party
 * attempt never leaks the mission's current state.
 */
export function evaluateMissionTransition(
  action: MissionAction,
  fromStatus: MissionStatus,
  actorRole: MissionActorRole,
): MissionTransitionResult {
  const rule = MISSION_TRANSITIONS[action];
  if (rule.actor !== actorRole) return { ok: false, reason: 'forbidden_actor' };
  if (!rule.from.includes(fromStatus)) return { ok: false, reason: 'invalid_transition' };
  return { ok: true, to: rule.to, verb: rule.verb };
}

/** The actions a given party may attempt (regardless of state) — for surfacing UI controls. */
export function missionActionsFor(role: MissionActorRole): MissionAction[] {
  return MISSION_ACTIONS.filter((a) => MISSION_TRANSITIONS[a].actor === role);
}

/** The actions a party may attempt RIGHT NOW (valid actor AND valid source state). */
export function availableMissionActions(
  fromStatus: MissionStatus,
  role: MissionActorRole,
): MissionAction[] {
  return missionActionsFor(role).filter((a) => MISSION_TRANSITIONS[a].from.includes(fromStatus));
}
